import * as React from 'react';
import { Outlet } from 'react-router-dom';

import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useRenewalCount } from '@/lib/api/domains';
import { remindersApi } from '@/lib/api/domains';
import { OfflineBanner } from '@/components/offline/offline-indicator';
import { CommandPalette, useCommandPalette } from './command-palette';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';

export function AppShell() {
  const { open, setOpen } = useCommandPalette();
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  // Sidebar counters. Both ask for one row and read the total, so the badges cost a
  // couple of hundred bytes rather than the full result set they are counting.
  const { data: renewalCount } = useRenewalCount(30);
  const { data: overdue } = remindersApi.useList({ status: 'overdue', perPage: 1 });

  const badges = {
    renewals: renewalCount,
    overdueReminders: overdue?.meta.total,
  };

  return (
    <div className="bg-background flex h-dvh overflow-hidden">
      <div className="hidden lg:block print:hidden">
        <Sidebar badges={badges} />
      </div>

      {/* On narrow screens the same sidebar is presented as a full-screen menu, so there
          is one navigation implementation rather than a desktop and a mobile copy. It
          covers the whole viewport (max-h-none: the dialog's default height cap would
          leave a 4rem gap at the bottom), so the sidebar renders its own X — there is no
          "outside" left to tap — and collapsible={false} keeps the desktop's persisted
          collapse preference from leaking an icon-only rail into the sheet. */}
      <Dialog open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <DialogContent
          showCloseButton={false}
          className="top-0 left-0 h-dvh max-h-none w-full max-w-none translate-x-0 translate-y-0 grid-rows-1 rounded-none border-0 p-0"
          aria-label="Navigation"
        >
          <Sidebar
            badges={badges}
            collapsible={false}
            onNavigate={() => setMobileNavOpen(false)}
            onClose={() => setMobileNavOpen(false)}
            className="w-full border-r-0"
          />
        </DialogContent>
      </Dialog>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onOpenSearch={() => setOpen(true)} onOpenMobileNav={() => setMobileNavOpen(true)} />
        <OfflineBanner />
        {/* Wider gutters and a longer run-out at the bottom: content that stops flush
            against the viewport edge is the single clearest tell of an unfinished
            layout, and the extra bottom padding keeps the last table row clear of the
            fold when a page is scrolled to the end. */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[100rem] px-4 pt-5 pb-16 sm:px-7 sm:pt-7">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette open={open} onOpenChange={setOpen} />
    </div>
  );
}
